import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  CopySkippedItemsModal,
  WizardModal,
  type WizardPrimaryAction,
} from '~/components/overlays';
import { wizardStepsFromTranslations } from '~/components/overlays/wizardStepsFromTranslations';
import { toastGraphQLError } from '~/utils';
import { useSubmitOnce } from '~/hooks/useSubmitOnce';
import { useAddBuyListToCart } from '~/api/buyListCart';
import { setSelectedLocationId } from '~/redux/reducers/cart';
import type { SkippedOrderItem } from '~/graphql/graphql';
import { SelectBuyListItemsStep } from '~/pages/buy-lists/sections/SelectBuyListItemsStep';
import type { BuyListDetailItem } from '~/pages/buy-lists/sections/types';
import { useBuyListItemSelection } from '~/pages/buy-lists/sections/useBuyListItemSelection';
import { useSummaryProducts } from '~/pages/buy-lists/sections/useSummaryProducts';
import { findSelectionConflicts } from '~/pages/buy-lists/sections/selectionConflicts';
import type { BuyListDetailTally } from '~/pages/buy-lists/sections/tallies/types';
import { SelectSellerFacilityStep } from '~/components/common/SelectSellerFacilityStep';
import { AddBuyListToCartSummaryStep } from './AddBuyListToCartSummaryStep';
import { useShipToLocations } from '~/components/common/useShipToLocations';
import type { SelectedSellerFacility } from '~/components/common/SelectSellerFacilityStep';

type AddBuyListToCartModalProps = {
  onClose: () => void;
  buyListId: string;
  items: BuyListDetailItem[];
  tallies: BuyListDetailTally[];
  initialLocationId: string;
  initialTransportVehicleId: string;
};

const STEP_IDS = ['items', 'seller', 'summary'] as const;

type Step = (typeof STEP_IDS)[number];

type SkippedItemsResult = {
  skippedItems: SkippedOrderItem[];
  hasCart: boolean;
};

export function AddBuyListToCartModal({
  onClose,
  buyListId,
  items,
  tallies,
  initialLocationId,
  initialTransportVehicleId,
}: Readonly<AddBuyListToCartModalProps>) {
  const { t } = useTranslation('inventory');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('items');
  const selection = useBuyListItemSelection(items, tallies);
  const { selectedItems, selectedTallies } = selection;
  const selectionConflicts = useMemo(
    () => findSelectionConflicts(selectedItems, selectedTallies),
    [selectedItems, selectedTallies]
  );
  const products = useSummaryProducts(selection);
  const [selectedFacility, setSelectedFacility] =
    useState<SelectedSellerFacility | null>(null);
  const [skippedItemsResult, setSkippedItemsResult] =
    useState<SkippedItemsResult | null>(null);

  const { locations: shipToLocations } = useShipToLocations();
  const isInitialLocationAllowed = shipToLocations.some(
    (location) => location.id === initialLocationId
  );
  const [chosenLocationId, setChosenLocationId] = useState<string | null>(null);
  const locationId =
    chosenLocationId ?? (isInitialLocationAllowed ? initialLocationId : null);
  const [transportVehicleId, setTransportVehicleId] = useState<string | null>(
    initialTransportVehicleId
  );

  const { mutate, isPending } = useAddBuyListToCart();
  const submitOnce = useSubmitOnce();

  const handleAddToCart = () => {
    if (selectedFacility === null || locationId === null) return;
    const sellerOrganizationFacilityId = selectedFacility.facilityId;
    submitOnce((onSettled) => {
      mutate(
        {
          buyListId,
          sellerOrganizationFacilityId,
          locationId,
          transportVehicleId,
          ...selection.toSelectionInputs(),
        },
        {
          onSuccess: (data) => {
            const result = data.addBuyListToCart;
            dispatch(setSelectedLocationId(locationId));
            if (result.skippedItems.length > 0) {
              setSkippedItemsResult({
                skippedItems: result.skippedItems,
                hasCart: result.cart != null,
              });
              return;
            }
            void navigate('/cart');
          },
          onError: toastGraphQLError,
          onSettled,
        }
      );
    });
  };

  const closeSkippedItems = () => {
    const shouldGoToCart =
      skippedItemsResult !== null && skippedItemsResult.hasCart;
    setSkippedItemsResult(null);
    if (shouldGoToCart) {
      void navigate('/cart');
      return;
    }
    onClose();
  };

  if (skippedItemsResult !== null) {
    return (
      <CopySkippedItemsModal
        skippedItems={skippedItemsResult.skippedItems}
        onOkay={closeSkippedItems}
        title={
          skippedItemsResult.hasCart
            ? t('inventory.buy_list.add_to_cart.skipped_some_title')
            : t('inventory.buy_list.add_to_cart.skipped_all_title')
        }
      />
    );
  }

  const steps = wizardStepsFromTranslations(
    t,
    'inventory.buy_list.add_to_cart',
    STEP_IDS
  );

  const primaryActionByStep: Record<Step, WizardPrimaryAction> = {
    items: {
      label: t('inventory.buy_list.add_to_cart.continue'),
      testId: 'add-to-cart-continue',
      disabled:
        selection.hasNothingSelected ||
        selection.hasLineWithoutQuantity ||
        selectionConflicts.size > 0,
      onClick: () => setStep('seller'),
    },
    seller: {
      label: t('inventory.buy_list.add_to_cart.next'),
      testId: 'add-to-cart-next',
      disabled: selectedFacility === null,
      onClick: () => setStep('summary'),
    },
    summary: {
      label: t('inventory.buy_list.add_to_cart.submit'),
      testId: 'add-to-cart-submit',
      loading: isPending,
      disabled:
        isPending || selection.hasNothingSelected || locationId === null,
      onClick: handleAddToCart,
    },
  };

  return (
    <WizardModal
      onClose={onClose}
      isPending={isPending}
      steps={steps}
      currentStepId={step}
      onStepChange={setStep}
      testIdPrefix="add-to-cart"
      backLabel={t('inventory.buy_list.add_to_cart.back')}
      primaryAction={primaryActionByStep[step]}
    >
      {step === 'items' && (
        <SelectBuyListItemsStep
          items={items}
          {...selection.itemsStepProps}
          tallySelection={selection.talliesSectionProps}
          conflictsByRowId={selectionConflicts}
        />
      )}
      {step === 'seller' && (
        <SelectSellerFacilityStep
          selectedFacilityId={
            selectedFacility === null ? null : selectedFacility.facilityId
          }
          onSelectFacility={setSelectedFacility}
        />
      )}
      {step === 'summary' && selectedFacility !== null && (
        <AddBuyListToCartSummaryStep
          products={products}
          selectedFacility={selectedFacility}
          locations={shipToLocations}
          locationId={locationId}
          transportVehicleId={transportVehicleId}
          onLocationChange={setChosenLocationId}
          onTransportVehicleChange={setTransportVehicleId}
        />
      )}
    </WizardModal>
  );
}
